import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus } from 'lucide-react';
import { CONSENT_FORM_COPY, EMPTY_STATES, ROLES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { ClassRoomSummary } from '@/types';

/**
 * 범용 전자동의서 — P5 후순위 모듈 (최소 구현)
 *
 * 교직원: 동의서 보내기 + 동의/미동의/미응답 현황판.
 * 학부모: 아이별 응답 — 버튼 한 번이 전자 서명(응답 시각 기록)이다.
 */
export function ConsentsPage() {
  const membership = useAuth((s) => s.membership);
  const isParent = membership?.role === ROLES.PARENT;

  return (
    <Screen>
      <AppBar title="동의서" back />
      {isParent ? <ParentView /> : <StaffView />}
    </Screen>
  );
}

// ── 학부모 ────────────────────────────────────────────────

interface MyConsentRow {
  formId: string;
  title: string;
  body: string;
  dueDate: string | null;
  child: { id: string; name: string };
  response: { agreed: boolean; comment: string | null; signedAt: string } | null;
}

function ParentView() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['consents', 'mine'],
    queryFn: () => apiData<MyConsentRow[]>('/consents/mine'),
  });

  const rows = data ?? [];

  return (
    <div className="flex flex-col gap-3 p-4">
      {isPending && <Spinner />}
      {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

      {!isPending && rows.length === 0 && (
        <EmptyState title={EMPTY_STATES.consent.title} body={EMPTY_STATES.consent.body} />
      )}

      {rows.map((row) => (
        <ParentConsentCard key={`${row.formId}-${row.child.id}`} row={row} />
      ))}
    </div>
  );
}

function ParentConsentCard({ row }: { row: MyConsentRow }) {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const respond = useMutation({
    mutationFn: (agreed: boolean) =>
      apiData(`/consents/${row.formId}/respond`, {
        method: 'POST',
        body: { childId: row.child.id, agreed, comment: comment || undefined },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['consents'] }),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '응답을 남기지 못했어요.'),
  });

  return (
    <Card>
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        <Chip tone="primary">{row.child.name}</Chip>
        {row.dueDate && (
          <span className="ml-auto text-sm text-ink-sub">{formatDate(row.dueDate)}까지</span>
        )}
      </div>
      <p className="font-semibold">{row.title}</p>
      <p className="mt-1 whitespace-pre-wrap text-base text-ink-sub">{row.body}</p>

      {row.response ? (
        <div className="mt-3 flex items-center gap-2">
          <Chip tone={row.response.agreed ? 'primary' : 'secondary'}>
            {row.response.agreed ? CONSENT_FORM_COPY.agreedChip : CONSENT_FORM_COPY.declinedChip}
          </Chip>
          <span className="text-sm text-ink-sub">
            {CONSENT_FORM_COPY.signedNote(formatDate(row.response.signedAt))}
          </span>
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          <Input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={CONSENT_FORM_COPY.commentPlaceholder}
          />
          {error && (
            <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              block
              icon={<Check size={16} />}
              loading={respond.isPending}
              onClick={() => respond.mutate(true)}
            >
              {CONSENT_FORM_COPY.agree}
            </Button>
            <Button
              block
              variant="outline"
              loading={respond.isPending}
              onClick={() => respond.mutate(false)}
            >
              {CONSENT_FORM_COPY.decline}
            </Button>
          </div>
          <p className="text-sm text-ink-sub">{CONSENT_FORM_COPY.legalNote}</p>
        </div>
      )}
    </Card>
  );
}

// ── 교직원 ────────────────────────────────────────────────

interface StaffConsentForm {
  id: string;
  title: string;
  body: string;
  dueDate: string | null;
  classRoom: { id: string; name: string } | null;
  responses: {
    id: string;
    agreed: boolean;
    comment: string | null;
    signedAt: string;
    child: { id: string; name: string };
  }[];
  summary: { total: number; agreed: number; declined: number; pending: number };
}

function StaffView() {
  const [composing, setComposing] = useState(false);
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['consents'],
    queryFn: () => apiData<StaffConsentForm[]>('/consents'),
  });

  const forms = data ?? [];

  return (
    <div className="flex flex-col gap-3 p-4">
      {!composing && (
        <Button variant="outline" icon={<Plus size={16} />} onClick={() => setComposing(true)}>
          동의서 보내기
        </Button>
      )}
      {composing && <ConsentComposer onClose={() => setComposing(false)} />}

      {isPending && <Spinner />}
      {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

      {!isPending && forms.length === 0 && !composing && (
        <EmptyState
          title="보낸 동의서가 없어요"
          body="현장학습·개인정보 같은 동의가 필요할 때 종이 없이 받아 보세요."
        />
      )}

      {forms.map((form) => (
        <Card key={form.id}>
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            <Chip tone="neutral">{form.classRoom?.name ?? '전체'}</Chip>
            {form.dueDate && (
              <span className="ml-auto text-sm text-ink-sub">{formatDate(form.dueDate)}까지</span>
            )}
          </div>
          <p className="font-semibold">{form.title}</p>
          <p className="mt-1 text-sm text-ink-sub">
            동의 <b className="tnum">{form.summary.agreed}</b> · 미동의{' '}
            <b className="tnum">{form.summary.declined}</b> · 미응답{' '}
            <b className="tnum">{form.summary.pending}</b> / 대상{' '}
            <b className="tnum">{form.summary.total}</b>명
          </p>
          {form.responses.length > 0 && (
            <ul className="mt-2 flex flex-col gap-1 border-t border-line pt-2">
              {form.responses.map((response) => (
                <li key={response.id} className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{response.child.name}</span>
                  <Chip tone={response.agreed ? 'primary' : 'secondary'}>
                    {response.agreed
                      ? CONSENT_FORM_COPY.agreedChip
                      : CONSENT_FORM_COPY.declinedChip}
                  </Chip>
                  {response.comment && (
                    <span className="min-w-0 flex-1 truncate text-ink-sub">{response.comment}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      ))}
    </div>
  );
}

function ConsentComposer({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [classRoomId, setClassRoomId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: classrooms } = useQuery({
    queryKey: ['classrooms'],
    queryFn: () => apiData<ClassRoomSummary[]>('/classrooms'),
  });

  const create = useMutation({
    mutationFn: () =>
      apiData('/consents', {
        method: 'POST',
        body: {
          title,
          body,
          classRoomId: classRoomId || undefined,
          dueDate: dueDate || undefined,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['consents'] });
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '보내지 못했어요.'),
  });

  return (
    <Card>
      <p className="mb-2 font-semibold">동의서 보내기</p>
      <div className="flex flex-col gap-3">
        <Field label="제목" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="예: 가을 현장학습 참가 동의"
          />
        </Field>
        <Field label="내용" required>
          <Textarea
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="일시·장소·비용 등 보호자가 판단할 내용을 적어 주세요."
          />
        </Field>
        <Field label="대상">
          <div className="scroll-x flex gap-2 overflow-x-auto">
            <Chip
              tone={classRoomId === '' ? 'primary' : 'neutral'}
              selected={classRoomId === ''}
              onClick={() => setClassRoomId('')}
            >
              전체
            </Chip>
            {(classrooms ?? []).map((room) => (
              <Chip
                key={room.id}
                tone={classRoomId === room.id ? 'primary' : 'neutral'}
                selected={classRoomId === room.id}
                onClick={() => setClassRoomId(room.id)}
              >
                {room.name}
              </Chip>
            ))}
          </div>
        </Field>
        <Field label="응답 기한">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <Button
            block
            loading={create.isPending}
            disabled={!title.trim() || !body.trim()}
            onClick={() => create.mutate()}
          >
            보내기
          </Button>
          <Button block variant="outline" onClick={onClose}>
            닫기
          </Button>
        </div>
      </div>
    </Card>
  );
}
